import { useEffect, useState } from "react";
import { Gift, Copy, Check, Linkedin } from "lucide-react";
import { toast } from "sonner";
import { getInvite, inviteLink } from "../../../lib/invite";
import { shareOnLinkedIn } from "../../../lib/social";
import { Section } from "./ProfileFields";

const fmt = (iso) => new Date(iso).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" });

function Stat({ value, label, testid }) {
  return (
    <div className="rounded-xl bg-canvas border border-line p-3 text-center">
      <div className="font-display text-2xl font-bold text-ink" data-testid={testid}>{value}</div>
      <div className="text-xs text-body">{label}</div>
    </div>
  );
}

export default function InviteFriends() {
  const [d, setD] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { getInvite(true).then(setD).catch(() => {}); }, []);
  if (!d) return null;
  const link = inviteLink(d.code);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success("Invite link copied."); } catch { toast.error("Couldn't copy. Select the link and copy it instead."); }
  };
  const share = () => shareOnLinkedIn(`I've been using GetInterviewed to build a keyword-rich CV and tailor it to each job. It's free for job seekers in Singapore, so give it a try: ${link} #GetInterviewed #JobSearch`);
  return (
    <Section title="Invite friends, get Career Journey free" testid="invite-friends"
      hint={`Every ${d.per_month} friends who join with your link and add their CV earn you 1 free month of Career Journey (up to ${d.max_months} months).`}>
      <div className="flex flex-col sm:flex-row gap-2">
        <label htmlFor="invite-link" className="sr-only">Your invite link</label>
        <input id="invite-link" data-testid="invite-link" readOnly value={link} onFocus={(e) => e.target.select()} className="gi-input flex-1 font-mono2 text-sm" />
        <div className="flex gap-2">
          <button data-testid="invite-copy" onClick={copy} className="gi-btn gi-btn-secondary flex-1 sm:flex-none">{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} Copy</button>
          <button data-testid="invite-share-linkedin" onClick={share} className="gi-btn gi-btn-primary flex-1 sm:flex-none"><Linkedin className="w-4 h-4" /> Share</button>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-3 gap-3">
        <Stat value={d.joined} label="friends joined" testid="invite-joined" />
        <Stat value={d.counted} label="added their CV" testid="invite-counted" />
        <Stat value={d.months_earned} label="free months earned" testid="invite-months" />
      </div>
      {d.months_earned < d.max_months && (
        <div className="mt-4" data-testid="invite-progress">
          <div className="flex justify-between text-sm"><span className="text-body">Towards your next free month</span><span className="font-semibold text-brand-text">{d.toward_next} of {d.per_month}</span></div>
          <div className="mt-2 h-2 rounded-full bg-subtle overflow-hidden"><div className="h-full bg-brand rounded-full transition-[width] duration-500" style={{ width: `${(100 * d.toward_next) / d.per_month}%` }} /></div>
        </div>
      )}
      <p className="mt-4 flex items-start gap-2 text-sm text-body" data-testid="invite-reward-note">
        <Gift className="w-4 h-4 text-kesh mt-0.5 shrink-0" aria-hidden="true" />
        {d.lifetime ? "You already have lifetime Career Journey, so this is just a nice way to help your friends."
          : d.subscriber ? "Each free month comes off your next monthly bill."
          : d.free_until ? `Career Journey is free for you until ${fmt(d.free_until)}.`
          : "Friends count once they've signed up with a verified email and added their CV."}
      </p>
    </Section>
  );
}
